import z, { ZodAny, ZodObject, ZodRawShape } from "zod";
import { aiToolsDownloadContainerFile } from "../api/api";
import { loadAnalysisResult } from "../state/ai/analysisResults";
import { AI_TOOL } from "../types";
import { getExcelVersion } from "../util/versioning";

const KNOWN_TOOLS: Record<AI_TOOL, string | { label: string, icon: string }> = {
  [AI_TOOL.GET_DATASET_META]: {
    label: "Fetching deal metadata",
    icon: "clipboard-data"
  },
  [AI_TOOL.DOWNLOAD_FILE]: {
    icon: "download",
    label: "Preparing file download"
  },
  [AI_TOOL.READ_RANGE]: {
    label: "Reading content from Excel",
    icon: "table"
  },
  [AI_TOOL.WRITE_RANGE]: {
    label: "Writing data",
    icon: "pen"
  },
  [AI_TOOL.EXECUTE_JS]: {
    label: "Running custom code",
    icon: "braces-asterisk"
  },
  [AI_TOOL.WEB_SEARCH]: {
    label: "Searching the web",
    icon: "globe"
  },
  [AI_TOOL.APPLY_WORKBOOK_PATCH]: {
    label: "Applying data to workbook",
    icon: "table"
  },
  [AI_TOOL.GET_WORKBOOK_FILE]: {
    label: "Reading your workbook",
    icon: "file-earmark-arrow-up"
  },
  [AI_TOOL.SUBMIT_ANALYSIS_RESULT]: {
    label: "Compiling your analysis",
    icon: "clipboard-data"
  }
}

const getToolLabel = (t: AI_TOOL): string => {
  const k = KNOWN_TOOLS[t];
  return typeof k === "string" ? k : k.label;
}

const TOOL_USE_TEXTS = [
  "Starting the algorithms",
  "Crunching the numbers",
  "Consulting the data gods",
  "Just a sec",
  "This reminds me of a deal I saw once",
  "Doing some thinking",
  "Let me consult my spreadsheets",
  "Hold tight, I'm on it",
  "Let me check my notes",
  "Putting on my thinking cap",
  "Running some calculations",
  "Checking latest market trends",
  "Let me just think for a moment",
  "Let me consult my inner circle",
  "Art is in the details",
  "Asking the magic 8 ball",
  "Thinking outside the box",
  "Bribing the algorithm",
  "Dividing by zero",
  "I need more coffee"
];

const FIRST_TURN_TOOLS = [
  "get_dataset_metadata",
  "web_search"
]

const execTool = async <T extends ZodRawShape, X extends ZodObject<T> | ZodAny>(s: X, i: unknown, fn: (a: z.infer<X>) => Promise<string>): Promise<string> => {
  try {
    console.log("RUNNING TOOL WITH INPUT: ", i);
    const d: any = s.parse(i);
    const result = await fn(d);
    console.log("TOOL RESULT: ", result);
    return result;
  }
  catch (err: unknown) {
    console.log(err);
    if (err instanceof Error) {
      return `Error: ${err.message}`
    }
    return "Error: Tool use failed";
  }
}

const createNonExcelTool = <T extends ZodRawShape, X extends ZodObject<T> | ZodAny>(
  s: X,
  fn: (a: z.infer<X>) => Promise<string>
) => (
  async (input: unknown) => await execTool(s, input, fn)
);

const createTool = <T extends ZodRawShape, X extends ZodObject<T> | ZodAny>(
  s: X,
  fn: (a: z.infer<X>, ctx: Excel.RequestContext) => Promise<string>
) => (
  async (input: unknown) => await execTool(
    s,
    input,
    async (i) => {
      if (!Excel) {
        return "Error: Chat is not running in excel";
      }
      return await Excel.run(
        async (ctx) => await fn(i, ctx)
      )
    }
  )
);

const DownloadFileArgs = z.object({
  filename: z.string(),
  mimeType: z.string(),
  content: z.string()
});

const WritePlan = z.object({
  guard: z.object({ titleIncludes: z.string() }).optional(),
  manualCalc: z.boolean().optional(),
  clears: z.array(z.object({
    sheetName: z.string(),
    range: z.string(),
    applyTo: z.enum(["contents", "formats", "all"]).default("contents")
  })).optional(),
  writes: z.array(z.object({
    sheetName: z.string(),
    range: z.string(),
    values: z.array(z.array(z.any()))
  })).optional(),
  formats: z.array(z.object({
    sheetName: z.string(),
    range: z.string(),
    numberFormat: z.string()
  })).optional(),
  recalc: z.enum(["full", "recalc"]).optional(),
  reads: z.array(z.object({
    key: z.string(),
    sheetName: z.string(),
    range: z.string()
  })).optional()
});

const ApplyWorkbookPatchArgs = z.object({
  file_id: z.string(),
  summary: z.string().optional()
});

const SubmitAnalysisResultArgs = z.object({
  file_id: z.string(),
  summary: z.string().optional()
});


const NON_EXCEL_TOOLS = {
  submit_analysis_result: createNonExcelTool(
    SubmitAnalysisResultArgs,
    async (a) => {
      const res = await loadAnalysisResult(a.file_id);

      switch (res.ok) {
        case false:
          return `Error: ${res.error}`;

        case true:
          break;
      }

      return `Success: the analysis was delivered to the user with ${res.data.findings.length} findings. Do not repeat the result in your reply.`;
    }
  ),
  download_file: createNonExcelTool(
    DownloadFileArgs,
    async (a) => {
      const typeString = a.mimeType.includes("charset") ? a.mimeType : `${a.mimeType};charset=utf-8;`;

      const blob = new Blob([a.content], { type: typeString });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", a.filename);
      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return `Success: File "${a.filename}" (${a.mimeType}) successfully downloaded by the client.`;
    }
  ),
}

const EXCEL_TOOLS = {
  apply_workbook_patch: createNonExcelTool(
    ApplyWorkbookPatchArgs,
    async (a) => {
      if (typeof Excel === "undefined") return "Error: not running in Excel";

      const res = await aiToolsDownloadContainerFile(a.file_id);

      switch (res.success) {
        case false:
          return `Error: download failed (${res.message})`;

        case true:
          break;
      }

      const plan = WritePlan.parse(
        typeof res.data === "string" ? JSON.parse(res.data) : res.data
      );

      return await Excel.run(async (ctx) => {

        const ws = (n: string) => ctx.workbook.worksheets.getItem(n);

        if (plan.guard) {
          const props = ctx.workbook.properties;
          props.load("title");
          await ctx.sync();
          const t = (props.title || "").toUpperCase().replace(/[-_]/g, " ").replace(/\s+/g, " ");
          if (!t.includes(plan.guard.titleIncludes.toUpperCase())) {
            return `Error: workbook guard failed. Title is "${props.title}". Nothing was written.`;
          }
        }

        ctx.application.load("calculationMode");
        await ctx.sync();
        const saved = ctx.application.calculationMode;

        try {

          if (plan.manualCalc) {
            ctx.application.calculationMode = Excel.CalculationMode.manual;
            await ctx.sync();
          }

          let cells = 0;

          const APPLY = {
            contents: Excel.ClearApplyTo.contents,
            formats: Excel.ClearApplyTo.formats,
            all: Excel.ClearApplyTo.all
          };

          for (const c of plan.clears ?? []) {
            ws(c.sheetName).getRange(c.range).clear(APPLY[c.applyTo]);
          }

          for (const w of plan.writes ?? []) {
            const rows = w.values.length;
            const cols = Math.max(...w.values.map(r => r.length));
            const target = ws(w.sheetName).getRange(w.range).getResizedRange(rows - 1, cols - 1);
            target.values = w.values.map(r =>
              r.length === cols ? r : [...r, ...Array(cols - r.length).fill(null)]
            );
            cells += rows * cols;
          }

          const fmts = (plan.formats ?? []).map(f => {
            const r = ws(f.sheetName).getRange(f.range);
            r.load("rowCount, columnCount");
            return { f, r };
          });

          if (fmts.length) await ctx.sync();

          for (const { f, r } of fmts) {
            r.numberFormat = Array.from(
              { length: r.rowCount },
              () => Array(r.columnCount).fill(f.numberFormat)
            );
          }

          await ctx.sync();

          if (plan.recalc) {
            ctx.application.calculationMode = Excel.CalculationMode.automatic;
            ctx.application.calculate(
              plan.recalc === "full" ? Excel.CalculationType.full : Excel.CalculationType.recalculate
            );
            await ctx.sync();
          }

          const out: Record<string, any> = { ok: true, cells };

          if (plan.reads?.length) {
            const loaded = plan.reads.map(r => {
              const rng = ws(r.sheetName).getRange(r.range);
              rng.load("values");
              return { key: r.key, rng };
            });
            await ctx.sync();
            out.reads = Object.fromEntries(loaded.map(l => [l.key, l.rng.values]));
          }

          return JSON.stringify(out);
        }
        finally {
          if (saved !== null) {
            ctx.application.calculationMode = saved;
            await ctx.sync();
          }
        }

      });
    }
  ),
  write_range: createTool(
    z.object({
      sheetName: z.string(),
      range: z.string(),
      values: z.array(z.array(z.any()))
    }),
    async (a, ctx) => {
      const range = ctx.workbook.worksheets.getItem(a.sheetName).getRange(a.range);
      range.values = a.values;
      await ctx.sync();
      return "OK";
    }
  ),
  read_range: createTool(
    z.object({
      sheetName: z.string(),
      range: z.string()
    }),
    async (a, ctx) => {
      const range = ctx.workbook.worksheets.getItem(a.sheetName).getRange(a.range);
      range.load(["values", "formulas"]);
      await ctx.sync();
      return JSON.stringify({ values: range.values, formulas: range.formulas });
    }
  ),
  execute_js: createTool(
    z.object({
      code: z.string()
    }),
    async (a, ctx) => {
      const fn = new Function("context", `return (async () => { ${a.code} })();`);
      const out = await fn(ctx);
      await ctx.sync();
      return JSON.stringify({ success: true, value: out });
    }
  ),
  get_dataset_metadata: createTool(z.any(), async (a, ctx) => {
    const worksheets = ctx.workbook.worksheets;
    const activeSheet = worksheets.getActiveWorksheet();
    const activeRange = ctx.workbook.getSelectedRange();

    worksheets.load("items/name, items/tables/items/name");
    activeSheet.load("name");
    activeRange.load("address");

    await ctx.sync();

    const sheetsWithRanges = worksheets.items.map(sheet => {
      const usedRange = sheet.getUsedRangeOrNullObject();
      usedRange.load(["rowCount", "columnCount", "isNullObject"]);
      return { sheet, usedRange };
    });

    await ctx.sync();

    const metadata = sheetsWithRanges.map(({ sheet, usedRange }) => ({
      sheetName: sheet.name,
      rowCount: usedRange.isNullObject ? 0 : usedRange.rowCount,
      columnCount: usedRange.isNullObject ? 0 : usedRange.columnCount,
      tables: sheet.tables.items.map(t => t.name)
    }));

    const meta = {
      activeSheetName: activeSheet.name,
      activeRangeAddress: activeRange.address,
      sheets: metadata
    };

    return JSON.stringify(meta);
  }),
}

const ALL_TOOLS = {
  ...NON_EXCEL_TOOLS,
  ...EXCEL_TOOLS
};

export default function useAiTools() {
  const v = getExcelVersion();

  const tools = v.isExcel ? ALL_TOOLS : NON_EXCEL_TOOLS;

  return {
    tools,
    downloadFile: NON_EXCEL_TOOLS.download_file
  }
}

type DownloadFileArgs = z.infer<typeof DownloadFileArgs>;

const getToolUseLabel = (t: string, b: any) => {
  if (t === "text_editor_code_execution" || t === "text_editor_code_execution_tool_result") {
    return {
      label: "Researching",
      icon: "book"
    };
  }

  if (!t.includes("tool_use")) {
    return null;
  }

  if (b.name === AI_TOOL.READ_RANGE) {
    const sheet = b.input?.sheetName;

    return {
      icon: "table",
      label: (
        (typeof sheet === "string" && sheet.trim())
          ? `Reading ${sheet}`
          : "Reading content from Excel"
      )
    }
  }

  const known = KNOWN_TOOLS[b.name as AI_TOOL];

  if (known) {
    if (typeof known === "string") {
      return {
        label: known,
        icon: "wrench"
      }
    }

    return known;
  }

  const seed = b.id || b.name || "tool";
  let hash = 0;

  for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash);

  const l = TOOL_USE_TEXTS[Math.abs(hash) % TOOL_USE_TEXTS.length]

  return {
    label: l,
    icon: "wrench"
  };
}

export {
  DownloadFileArgs, FIRST_TURN_TOOLS, getToolLabel, getToolUseLabel, KNOWN_TOOLS
};

